/// <amd-module name="Repos/ChangesActions/Components/PortChangesPanel" />
import * as React from "react";
import * as Resources from "Repos/ChangesActions/Resources";
import { GitRef } from "Repos/Common/Contracts/Git";
import { getInvalidBranchNameMessage, refToBranchName } from "Repos/Common/Util/Ref";
import { branchToVersionDescriptor } from "Repos/Common/Util/Version";
import { VersionDropdown } from "Repos/VersionDropdown/Components/VersionDropdown";
import { IVersionDropdownItem } from "Repos/VersionDropdown/Components/VersionDropdown.Props";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { ProgressBar } from "VSSUI/ProgressBar";
import { TextField } from "VSSUI/TextField";

export interface IPortChangesCompletionOptions {
    targetBranch: string;
    topicBranchName: string;
}

export interface IPortChangesPanelProps {
    cherryPickAsSingleCommit?: ObservableValue<boolean>;
    getBranchByName: (branchName: string) => Promise<GitRef | undefined>;
    errorMessage: IReadonlyObservableValue<string>;
    initialTargetBranch?: string;
    inProgressLabel: string;
    onDismiss: (completionOptions: IPortChangesCompletionOptions | undefined) => void;
    panelCompleteButtonText: string;
    panelDescription: string;
    panelTitle: string;
    pctComplete: IReadonlyObservableValue<number>;
    showCherryPickCheckbox?: boolean;
    showOverlay: IReadonlyObservableValue<boolean>;
    sourceBranch: string;
    topicBranchNameFormat: string;
}

export class PortChangesPanel extends VssComponent<IPortChangesPanelProps> {
    private fieldsValid = new ObservableValue(true);
    private targetBranchName = new ObservableValue("");
    private topicBranchError = new ObservableValue("");
    private topicBranchName = new ObservableValue("");
    private timerManagement = new TimerManagement();
    private throttledUpdateValid: () => void;
    private checkboxChecked: ObservableValue<boolean>;

    constructor(props: IPortChangesPanelProps, context: IVssComponentContext) {
        super(props, context);

        this.throttledUpdateValid = this.timerManagement.debounce(this.updateValid, 500);
        this.checkboxChecked = this.props.cherryPickAsSingleCommit || new ObservableValue<boolean>(false);

        this.targetBranchName.value = props.initialTargetBranch || "";
        if (props.initialTargetBranch) {
            this.populateTopicBranchName(props.initialTargetBranch);
        }

        this.updateValid();
    }

    public render() {
        const {
            errorMessage,
            initialTargetBranch,
            inProgressLabel,
            panelCompleteButtonText,
            panelDescription,
            panelTitle,
            pctComplete,
            showCherryPickCheckbox,
            showOverlay
        } = this.props;
        return (
            <Observer errorMessage={errorMessage} showOverlay={showOverlay} valid={this.fieldsValid}>
                {(props: { errorMessage?: string; showOverlay: boolean; valid: boolean }) => {
                    const footerButtons: IButtonProps[] = [
                        {
                            id: "cancel",
                            onClick: () => this.onDismiss(),
                            text: Resources.Cancel
                        },
                        {
                            id: "complete",
                            disabled: !props.valid,
                            onClick: () => this.onComplete(),
                            primary: true,
                            text: panelCompleteButtonText
                        }
                    ];

                    return (
                        <Panel
                            description={panelDescription}
                            footerButtonProps={footerButtons}
                            onDismiss={this.onDismiss}
                            overlayContent={
                                props.showOverlay ? (
                                    <div className="flex-column rhythm-vertical-8">
                                        <span>{inProgressLabel}</span>
                                        <ProgressBar currentValue={pctComplete} maxValue={1} />
                                    </div>
                                ) : undefined
                            }
                            titleProps={{ text: panelTitle }}
                        >
                            <div className="flex-column flex-grow scroll-hidden rhythm-vertical-16">
                                {props.errorMessage && <MessageCard severity={MessageCardSeverity.Error}>{props.errorMessage}</MessageCard>}

                                <FormItem label={Resources.TargetBranch}>
                                    <VersionDropdown
                                        initiallySelectedVersion={initialTargetBranch ? branchToVersionDescriptor(initialTargetBranch) : undefined}
                                        onSelect={this.onTargetBranchChanged}
                                        placeholder={Resources.TargetBranchPlaceholder}
                                        providerOptions={{
                                            viewTagsPivot: false
                                        }}
                                    />
                                </FormItem>

                                <Observer targetBranchError={this.topicBranchError}>
                                    {(props: { targetBranchError: string }) => {
                                        return (
                                            <FormItem
                                                error={props.targetBranchError ? true : false}
                                                label={Resources.TopicBranchName}
                                                message={props.targetBranchError}
                                            >
                                                <TextField
                                                    onChange={(e, val) => {
                                                        this.topicBranchName.value = val;
                                                        this.throttledUpdateValid();
                                                    }}
                                                    value={this.topicBranchName}
                                                />
                                            </FormItem>
                                        );
                                    }}
                                </Observer>

                                {showCherryPickCheckbox && (
                                    <Checkbox
                                        checked={this.checkboxChecked}
                                        onChange={(event, checked) => (this.checkboxChecked.value = checked)}
                                        label={Resources.CherryPickAsSingleCommit}
                                    />
                                )}
                            </div>
                        </Panel>
                    );
                }}
            </Observer>
        );
    }

    private onTargetBranchChanged = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IVersionDropdownItem> | null): void => {
        this.targetBranchName.value = item && item.data ? item.data.versionDescriptor.version : "";
        this.populateTopicBranchName(this.targetBranchName.value);
        this.updateValid();
    };

    private populateTopicBranchName(targetBranchName: string): void {
        const targetBranchNameWithoutSlashes = targetBranchName.split("/").join("-");
        this.topicBranchName.value = format(this.props.topicBranchNameFormat, this.props.sourceBranch, targetBranchNameWithoutSlashes);
    }

    private updateValid = (): void => {
        const topicBranchName = this.topicBranchName.value;
        let topicBranchError = "";
        if (!topicBranchName) {
            topicBranchError = Resources.Required;
        } else {
            topicBranchError = getInvalidBranchNameMessage(topicBranchName) || "";
            if (!topicBranchError) {
                this.props.getBranchByName(topicBranchName).then(branch => {
                    if (topicBranchName === this.topicBranchName.value && branch) {
                        this.fieldsValid.value = false;
                        this.topicBranchError.value = format(Resources.TopicBranchAlreadyExists, refToBranchName(branch.name));
                    }
                });
            }
        }

        if (this.topicBranchError.value !== topicBranchError) {
            this.topicBranchError.value = topicBranchError;
        }

        const valid = Boolean(this.targetBranchName.value && !topicBranchError);

        if (this.fieldsValid.value !== valid) {
            this.fieldsValid.value = valid;
        }
    };

    private onDismiss = () => {
        this.props.onDismiss(undefined);
    };

    private onComplete = () => {
        this.props.onDismiss({
            targetBranch: this.targetBranchName.value,
            topicBranchName: this.topicBranchName.value
        });
    };
}
